import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import useTranslations from "@/shared/lang/useTranslations";

import type { UserListItem } from "../types/users-api.types";

export interface DeleteUserDialogProps {
  user: UserListItem | null;
  isBusy: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * Deleting a person.
 *
 * The copy states the three consequences that are not obvious and that the operator cannot discover any other
 * way: the delete is soft (recoverable only by engineering), every session of theirs is revoked immediately,
 * and **their email address stays taken** — the unique constraint carries no `deleted_at` predicate, so the
 * address can never be reused. That last one would otherwise be discovered by failing to re-create someone.
 *
 * There is no self-deletion guard on the backend, so the caller must not render this for the current user.
 */
export function DeleteUserDialog({ user, isBusy, onConfirm, onOpenChange }: DeleteUserDialogProps) {
  const { t } = useTranslations("users");

  return (
    <ColocaAlertDialog
      open={user !== null}
      onOpenChange={onOpenChange}
      title={t("delete_title", { name: user?.full_name ?? "" })}
      description={t("delete_body")}
      confirmLabel={t("delete_confirm")}
      confirmVariant="destructive"
      confirmDisabled={isBusy}
      cancelLabel={t("action_cancel")}
      onConfirm={onConfirm}
    >
      <p className="text-sm text-warning">{t("delete_email_note")}</p>
    </ColocaAlertDialog>
  );
}
